import {
  JudgeScheduleTimeline,
  buildConcurrentDay,
  buildJudgeScheduleTimeline,
  ceilToFiveMinuteInterval,
  showTrialToken,
} from "@eagerdog/judge-schedule-util";
import { IEvent, IRunningOrder, IRunningOrderDogClass, IShow, isOfferedShowClass } from "@eagerdog/interfaces";
import moment from "moment-timezone";

import { findJudgeConflicts, JudgeSpan } from "./conflicts";
import { Constants, getRunningTimeMinutes } from "@eagerdog/constants";

import { childLevelsOf, classLabel, classTooltip, formatMinutes, isBreak, isGapBreak } from "./labels";

const JUDGE_CAPACITY_MINUTES = 8 * 60;

export interface ScheduleResource {
  id: string;
  parentId?: string;
  title: string;
  order: number;
  entries: string;
  minPerEntry: string;
  estStart: string;
  total: string;
  extendedProps: {
    kind: "trial" | "ring" | "judge" | "class" | "break";
    showId: string;
    ring?: string;
    judgeIndex?: number;
    classIndex?: number;
    canDeleteRing?: boolean;
    overCapacity?: boolean;
    conflict?: boolean;
    grouped?: boolean;
    judgeKey?: string;
  };
}

export interface ScheduleEventModel {
  id: string;
  resourceId: string;
  title: string;
  start: string;
  end: string;
  editable: boolean;
  durationEditable: boolean;
  resourceEditable: boolean;
  startEditable: boolean;
  overlap: boolean;
  classNames: string[];
  extendedProps: {
    kind: "trial" | "ring" | "judge" | "class" | "break";
    showId: string;
    ring: string;
    judgeIndex: number;
    classIndex?: number;
    tooltip: string;
    conflict?: boolean;
    /** Shortest bar length: time per entry × entries. Breaks use 5. */
    minMinutes?: number;
    minutes?: number;
    startMs?: number;
    /** Minutes of hidden gap immediately before this bar. Dragging earlier cannot pass it. */
    slackBeforeMinutes?: number;
    judgeKey?: string;
  };
}

export interface ScheduleModel {
  resources: ScheduleResource[];
  events: ScheduleEventModel[];
  slotMinTime: string;
  slotMaxTime: string;
  conflictCount: number;
  timelines: Map<string, JudgeScheduleTimeline>;
}

function showId(show: IShow): string {
  return show._id?.toString() || "";
}

function formatClock(date: Date, timezone: string, round: boolean): string {
  const shown = round ? ceilToFiveMinuteInterval(date, timezone) : date;
  return moment.tz(shown, timezone).format("h:mm A");
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60 * 1000);
}

/** When rounding is on, each class starts on a 5-minute mark after the previous class ends, and the bar uses that same clock. */
function roundedRingClock(ringStart: Date, judges: { tiles: { minutes: number }[] }[], timezone: string) {
  let cursor = ceilToFiveMinuteInterval(ringStart, timezone);
  return judges.map((judge) => {
    const start = cursor;
    const tiles = judge.tiles.map((tile) => {
      const tileStart = cursor;
      const tileEnd = addMinutes(tileStart, tile.minutes);
      cursor = ceilToFiveMinuteInterval(tileEnd, timezone);
      return { start: tileStart, end: tileEnd };
    });
    return { start, end: tiles.length ? tiles[tiles.length - 1].end : start, tiles };
  });
}

/** Hidden Gap minutes at the front of a judge. Dragging the judge earlier can only use this up. */
function leadingGapMinutes(classes: IRunningOrderDogClass[] = []): number {
  let minutes = 0;
  for (const dogClass of classes) {
    if (!isGapBreak(dogClass)) break;
    minutes += dogClass.duration || 0;
  }
  return minutes;
}

/**
 * Let a show or ring bar be dragged sideways to change its start. The length stays put.
 * resourceEditable stays on so the drag still counts when the pointer slips onto a child
 * row; the drop handler only uses the new time.
 */
function withStartDrag(event: ScheduleEventModel, slackBeforeMinutes?: number): ScheduleEventModel {
  return {
    ...event,
    editable: true,
    startEditable: true,
    durationEditable: false,
    resourceEditable: true,
    extendedProps: {
      ...event.extendedProps,
      startMs: new Date(event.start).getTime(),
      slackBeforeMinutes,
    },
  };
}

/** A judge starts at their first class or break; leading Gap idle time only pushes that start later. */
function firstNonGapStart(tiles: { dogClass?: IRunningOrderDogClass; start: Date }[], fallback: Date): Date {
  const first = tiles.find((tile) => tile.dogClass && !isGapBreak(tile.dogClass));
  return first?.start || fallback;
}

function slotTime(date: Date, timezone: string): { hour: number; minute: number } {
  const parts = moment.tz(date, timezone);
  return { hour: parts.hour(), minute: parts.minute() };
}

function pad(value: number): string {
  return value.toString().padStart(2, "0");
}

export function isClassRowVisible(
  dogClass: IRunningOrderDogClass,
  hideEmpty: boolean,
  show?: Pick<IShow, "show_type" | "show_elements">
): boolean {
  if (dogClass.group_with) {
    return false;
  }
  if (isGapBreak(dogClass)) {
    return false;
  }
  if (isBreak(dogClass)) {
    return true;
  }
  if (!dogClass.level && !dogClass.show_element) {
    return false;
  }
  // Junior Showmanship is one class with no level; other level-less rows are placeholders.
  if (!dogClass.level && dogClass.show_element !== Constants.dog_class_element.junior_showmanship) {
    return false;
  }
  if (
    show?.show_type === Constants.show_type.scentwork &&
    !isOfferedShowClass(show.show_elements, dogClass)
  ) {
    return false;
  }
  if (hideEmpty && !(dogClass.entries && dogClass.entries > 0)) {
    return false;
  }
  return true;
}

export function buildScheduleModel(
  shows: IShow[],
  allShows: IShow[],
  event: IEvent,
  hideEmpty: boolean
): ScheduleModel {
  const timezone = event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const resources: ScheduleResource[] = [];
  const events: ScheduleEventModel[] = [];
  const spans: JudgeSpan[] = [];
  const timelines = new Map<string, JudgeScheduleTimeline>();
  let order = 0;
  let earliest: Date | undefined;
  let latest: Date | undefined;

  const note = (date: Date) => {
    if (!earliest || date.getTime() < earliest.getTime()) earliest = date;
    if (!latest || date.getTime() > latest.getTime()) latest = date;
  };

  if (event.concurrent_schedule) {
    buildConcurrentScheduleModel(shows, event, hideEmpty, resources, events, spans, note);
  }

  if (!event.concurrent_schedule) shows.forEach((show) => {
    const id = showId(show);
    const timeline = buildJudgeScheduleTimeline(show, allShows);
    timelines.set(id, timeline);
    const trialId = `show:${id}`;
    const round = !!show.round_start_times_to_5;
    const trialEntries = timeline.rings.reduce((sum, ring) => sum + ring.judges.reduce((judgeSum, judge) => {
      const running = (show.rings || []).find((item) => item.ring === ring.ring)?.running_order?.[judge.judgeIndex];
      return judgeSum + entryTotal(running);
    }, 0), 0);
    const trialMinutes = timeline.rings.reduce((sum, ring) => sum + ring.totalMinutes, 0);

    resources.push({
      id: trialId,
      title: `${show.show_type} ${show.show_name}`.trim(),
      order: order++,
      entries: String(trialEntries),
      minPerEntry: "",
      estStart: formatClock(timeline.showStart, timezone, round),
      total: formatMinutes(trialMinutes),
      extendedProps: { kind: "trial", showId: id },
    });
    note(timeline.showStart);
    note(timeline.showEnd);
    const ringOffsets = (show.rings || []).map((item) => Math.max(0, item.start_offset_minutes || 0));
    events.push(withStartDrag(
      spanEvent(trialId, "trial", id, "", -1, timeline.showStart, timeline.showEnd, `${show.show_type} ${show.show_name}`, formatMinutes(trialMinutes)),
      show.trailing_show && ringOffsets.length ? Math.min(...ringOffsets) : undefined
    ));

    timeline.rings.forEach((ringSlot) => {
      const ringId = `ring:${id}:${ringSlot.ring}`;
      const ring = (show.rings || []).find((item) => item.ring === ringSlot.ring);
      const judges = ring?.running_order || [];
      resources.push({
        id: ringId,
        parentId: trialId,
        title: ringSlot.ring,
        order: order++,
        entries: String(judges.reduce((sum, judge) => sum + entryTotal(judge), 0)),
        minPerEntry: "",
        estStart: formatClock(ringSlot.start, timezone, round),
        total: formatMinutes(ringSlot.totalMinutes),
        extendedProps: {
          kind: "ring",
          showId: id,
          ring: ringSlot.ring,
          canDeleteRing: judges.length === 0 && (show.rings || []).length > 1,
        },
      });
      events.push(withStartDrag(
        spanEvent(ringId, "ring", id, ringSlot.ring, -1, ringSlot.start, ringSlot.end, ringSlot.ring, formatMinutes(ringSlot.totalMinutes)),
        Math.max(0, ring?.start_offset_minutes || 0)
      ));

      ringSlot.judges.forEach((judgeSlot) => {
        const judge = judges[judgeSlot.judgeIndex];
        const judgeId = `judge:${id}:${ringSlot.ring}:${judgeSlot.judgeIndex}`;
        const overCapacity = judgeSlot.judgingMinutes > JUDGE_CAPACITY_MINUTES;
        const number = judgeSlot.judgeNumber ? ` (${judgeSlot.judgeNumber})` : "";
        const judgeStart = firstNonGapStart(
          judgeSlot.classes.map((classSlot) => ({ dogClass: judge?.dog_classes?.[classSlot.classIndex], start: classSlot.start })),
          judgeSlot.start
        );
        resources.push({
          id: judgeId,
          parentId: ringId,
          title: `${judgeSlot.judgeName}${number}`,
          order: order++,
          entries: String(entryTotal(judge)),
          minPerEntry: "",
          estStart: formatClock(judgeStart, timezone, round),
          total: formatMinutes(judgeSlot.judgingMinutes),
          extendedProps: {
            kind: "judge",
            showId: id,
            ring: ringSlot.ring,
            judgeIndex: judgeSlot.judgeIndex,
            overCapacity,
          },
        });
        spans.push({
          id: judgeId,
          judgeName: judgeSlot.judgeName || "",
          startMs: judgeStart.getTime(),
          endMs: judgeSlot.end.getTime(),
        });
        const judgeEvent = spanEvent(judgeId, "judge", id, ringSlot.ring, judgeSlot.judgeIndex, judgeStart, judgeSlot.end, `${judgeSlot.judgeName}${number}`, formatMinutes(judgeSlot.judgingMinutes));
        events.push({
          ...judgeEvent,
          editable: true,
          startEditable: true,
          resourceEditable: true,
          durationEditable: false,
          classNames: ["jsb-judge", overCapacity ? "jsb-over" : ""].filter(Boolean),
          extendedProps: {
            ...judgeEvent.extendedProps,
            startMs: judgeStart.getTime(),
            slackBeforeMinutes: leadingGapMinutes(judge?.dog_classes),
          },
        });

        const classes = judge?.dog_classes || [];
        judgeSlot.classes.forEach((classSlot) => {
          const dogClass = classes[classSlot.classIndex];
          if (!dogClass || !isClassRowVisible(dogClass, hideEmpty, show)) {
            return;
          }
          const classId = `class:${id}:${ringSlot.ring}:${judgeSlot.judgeIndex}:${classSlot.classIndex}`;
          const children = childLevelsOf(dogClass, classes);
          const label = classLabel(event, show, dogClass, children);
          const startLabel = formatClock(classSlot.start, timezone, round);
          const breakTile = isBreak(dogClass);
          resources.push({
            id: classId,
            parentId: judgeId,
            title: label,
            order: order++,
            entries: breakTile ? "" : String(dogClass.entries || 0),
            minPerEntry: breakTile ? String(dogClass.duration || 0) : (dogClass.duration != null ? dogClass.duration.toFixed(1) : ""),
            estStart: startLabel,
            total: breakTile ? formatMinutes(dogClass.duration || 0) : formatMinutes(classSlot.minutes),
            extendedProps: {
              kind: breakTile ? "break" : "class",
              showId: id,
              ring: ringSlot.ring,
              judgeIndex: judgeSlot.judgeIndex,
              classIndex: classSlot.classIndex,
              grouped: children.length > 0,
            },
          });
          const end = classSlot.minutes > 0 ? classSlot.end : new Date(classSlot.start.getTime() + 60 * 1000);
          const perEntry = dogClass.duration && dogClass.duration > 0
            ? dogClass.duration
            : (getRunningTimeMinutes(event.sanctioning_club, show.show_type, dogClass.show_element, dogClass.level) || 0);
          const minMinutes = breakTile ? 5 : Math.round(perEntry * (dogClass.entries || 0) * 10) / 10;
          const previous = classes[classSlot.classIndex - 1];
          const slackBeforeMinutes = previous && isGapBreak(previous) ? (previous.duration || 0) : 0;
          note(classSlot.start);
          note(end);
          events.push({
            id: `evt:${classId}`,
            resourceId: classId,
            title: label,
            start: classSlot.start.toISOString(),
            end: end.toISOString(),
            editable: true,
            durationEditable: breakTile,
            resourceEditable: true,
            startEditable: true,
            overlap: true,
            classNames: [breakTile ? "jsb-break" : "jsb-class", children.length ? "jsb-grouped" : ""].filter(Boolean),
            extendedProps: {
              kind: breakTile ? "break" : "class",
              showId: id,
              ring: ringSlot.ring,
              judgeIndex: judgeSlot.judgeIndex,
              classIndex: classSlot.classIndex,
              tooltip: classTooltip(event, show, dogClass, startLabel, children),
              minMinutes,
              minutes: classSlot.minutes,
              startMs: classSlot.start.getTime(),
              slackBeforeMinutes,
            },
          });
        });
      });
    });
  });

  const conflicting = findJudgeConflicts(spans);
  resources.forEach((resource) => {
    if (conflicting.has(resource.id)) {
      resource.extendedProps.conflict = true;
    }
  });
  events.forEach((eventModel) => {
    if (conflicting.has(eventModel.resourceId) && eventModel.extendedProps.kind === "judge") {
      eventModel.extendedProps.conflict = true;
      eventModel.classNames.push("jsb-conflict");
      eventModel.title = `⚠ ${eventModel.title}`;
    }
  });

  const contentStart = earliest ? slotTime(earliest, timezone) : { hour: 8, minute: 0 };
  const max = latest ? slotTime(new Date(latest.getTime() + 60 * 60 * 1000), timezone) : { hour: 20, minute: 0 };
  const contentMinutes = contentStart.hour * 60 + contentStart.minute;
  const minMinutes = Math.floor(Math.min(contentMinutes, 8 * 60) / 60) * 60;
  const maxMinutes = Math.max(max.hour * 60 + max.minute, 20 * 60);

  return {
    resources,
    events,
    slotMinTime: `${pad(Math.floor(minMinutes / 60))}:${pad(minMinutes % 60)}:00`,
    slotMaxTime: `${pad(Math.min(24, Math.ceil(maxMinutes / 60)))}:00:00`,
    conflictCount: Math.floor(conflicting.size / 2) || (conflicting.size ? 1 : 0),
    timelines,
  };
}

function entryTotal(judge?: IRunningOrder): number {
  if (!judge) return 0;
  return (judge.dog_classes || []).reduce((sum, dogClass) => {
    if (isBreak(dogClass) || dogClass.group_with) return sum;
    return sum + (dogClass.entries || 0);
  }, 0);
}

function spanEvent(
  resourceId: string,
  kind: "trial" | "ring" | "judge",
  showId: string,
  ring: string,
  judgeIndex: number,
  start: Date,
  end: Date,
  title: string,
  total: string
): ScheduleEventModel {
  const safeEnd = end.getTime() > start.getTime() ? end : new Date(start.getTime() + 60 * 1000);
  return {
    id: `evt:${resourceId}`,
    resourceId,
    title: `${title}  ${total}`,
    start: start.toISOString(),
    end: safeEnd.toISOString(),
    editable: false,
    durationEditable: false,
    resourceEditable: false,
    startEditable: false,
    overlap: true,
    classNames: [`jsb-${kind}`],
    extendedProps: { kind, showId, ring, judgeIndex, tooltip: `${title} · ${total}` },
  };
}

export function sameLocalDay(a: string | Date, b: string | Date): boolean {
  return moment(a).format("YYYY-MM-DD") === moment(b).format("YYYY-MM-DD");
}

function buildConcurrentScheduleModel(
  shows: IShow[],
  event: IEvent,
  hideEmpty: boolean,
  resources: ScheduleResource[],
  events: ScheduleEventModel[],
  spans: JudgeSpan[],
  note: (date: Date) => void
) {
  const timezone = event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const prefixTrials = shows.length > 1;
  let order = 0;
  buildConcurrentDay(shows).forEach((ringSlot) => {
    const ringId = `cring:${ringSlot.ring}`;
    const ringRound = shows.some((show) => show.round_start_times_to_5);
    const roundedJudges = ringRound ? roundedRingClock(ringSlot.start, ringSlot.judges, timezone) : null;
    const displayedRingStart = roundedJudges?.[0]?.start || ringSlot.start;
    const displayedRingEnd = roundedJudges?.length ? roundedJudges[roundedJudges.length - 1].end : ringSlot.end;
    const ringEntries = ringSlot.judges.reduce((sum, judge) => sum + judge.tiles.reduce((tileSum, tile) => {
      if (isBreak(tile.dogClass) || tile.dogClass.group_with) return tileSum;
      return tileSum + (tile.dogClass.entries || 0);
    }, 0), 0);
    resources.push({
      id: ringId,
      title: ringSlot.ring,
      order: order++,
      entries: String(ringEntries),
      minPerEntry: "",
      estStart: formatClock(displayedRingStart, timezone, false),
      total: formatMinutes(ringSlot.totalMinutes),
      extendedProps: { kind: "ring", showId: ringSlot.judges[0]?.sources[0]?.showId || "", ring: ringSlot.ring },
    });
    note(displayedRingStart);
    note(displayedRingEnd);
    events.push(spanEvent(ringId, "ring", ringSlot.judges[0]?.sources[0]?.showId || "", ringSlot.ring, -1, displayedRingStart, displayedRingEnd, ringSlot.ring, formatMinutes(ringSlot.totalMinutes)));

    ringSlot.judges.forEach((judgeSlot, judgeOffset) => {
      const judgeId = `cjudge:${ringSlot.ring}:${judgeSlot.key}`;
      const number = judgeSlot.judgeNumber ? ` (${judgeSlot.judgeNumber})` : "";
      const overCapacity = judgeSlot.judgingMinutes > JUDGE_CAPACITY_MINUTES;
      const primary = judgeSlot.sources[0];
      const displayedJudge = roundedJudges?.[judgeOffset];
      const judgeStart = firstNonGapStart(
        judgeSlot.tiles.map((tile, tileIndex) => ({ dogClass: tile.dogClass, start: displayedJudge?.tiles[tileIndex]?.start || tile.start })),
        displayedJudge?.start || judgeSlot.start
      );
      const judgeEnd = displayedJudge?.end || judgeSlot.end;
      resources.push({
        id: judgeId,
        parentId: ringId,
        title: `${judgeSlot.judgeName}${number}`,
        order: order++,
        entries: String(judgeSlot.tiles.reduce((sum, tile) => {
          if (isBreak(tile.dogClass) || tile.dogClass.group_with) return sum;
          return sum + (tile.dogClass.entries || 0);
        }, 0)),
        minPerEntry: "",
        estStart: formatClock(judgeStart, timezone, false),
        total: formatMinutes(judgeSlot.judgingMinutes),
        extendedProps: {
          kind: "judge",
          showId: primary?.showId || "",
          ring: ringSlot.ring,
          judgeIndex: primary?.judgeIndex,
          overCapacity,
          judgeKey: judgeSlot.key,
        },
      });
      spans.push({
        id: judgeId,
        judgeName: judgeSlot.judgeName || "",
        startMs: judgeStart.getTime(),
        endMs: judgeEnd.getTime(),
      });
      events.push({
        ...spanEvent(judgeId, "judge", primary?.showId || "", ringSlot.ring, primary?.judgeIndex ?? -1, judgeStart, judgeEnd, `${judgeSlot.judgeName}${number}`, formatMinutes(judgeSlot.judgingMinutes)),
        editable: false,
        classNames: ["jsb-judge", overCapacity ? "jsb-over" : ""].filter(Boolean),
        extendedProps: {
          kind: "judge",
          showId: primary?.showId || "",
          ring: ringSlot.ring,
          judgeIndex: primary?.judgeIndex ?? -1,
          tooltip: `${judgeSlot.judgeName}${number} · ${formatMinutes(judgeSlot.judgingMinutes)}`,
          judgeKey: judgeSlot.key,
        },
      });

      judgeSlot.tiles.forEach((tile, tileIndex) => {
        const dogClass = tile.dogClass;
        const show = shows.find((item) => (item._id?.toString() || "") === tile.showId);
        if (!show || !isClassRowVisible(dogClass, hideEmpty, show)) return;
        const classes = (show.rings || []).find((item) => item.ring === tile.ring)?.running_order?.[tile.judgeIndex]?.dog_classes || [];
        const children = childLevelsOf(dogClass, classes);
        const label = classLabel(event, show, dogClass, children);
        const displayedTile = displayedJudge?.tiles[tileIndex];
        const tileStart = displayedTile?.start || tile.start;
        const tileEnd = displayedTile?.end || tile.end;
        const startLabel = formatClock(tileStart, timezone, false);
        const breakTile = isBreak(dogClass);
        const barTitle = breakTile
          ? label
          : prefixTrials
            ? `${show.show_type} ${showTrialToken(show.show_name)} - ${label}`
            : `${show.show_type} - ${label}`;
        const classId = `cclass:${tile.showId}:${tile.ring}:${tile.judgeIndex}:${tile.classIndex}`;
        resources.push({
          id: classId,
          parentId: judgeId,
          title: barTitle,
          order: order++,
          entries: breakTile ? "" : String(dogClass.entries || 0),
          minPerEntry: breakTile ? String(dogClass.duration || 0) : (dogClass.duration != null ? dogClass.duration.toFixed(1) : ""),
          estStart: startLabel,
          total: breakTile ? formatMinutes(dogClass.duration || 0) : formatMinutes(tile.minutes),
          extendedProps: {
            kind: breakTile ? "break" : "class",
            showId: tile.showId,
            ring: tile.ring,
            judgeIndex: tile.judgeIndex,
            classIndex: tile.classIndex,
            grouped: children.length > 0,
            judgeKey: judgeSlot.key,
          },
        });
        const end = tile.minutes > 0 ? tileEnd : new Date(tileStart.getTime() + 60 * 1000);
        const perEntry = dogClass.duration && dogClass.duration > 0
          ? dogClass.duration
          : (getRunningTimeMinutes(event.sanctioning_club, show.show_type, dogClass.show_element, dogClass.level) || 0);
        const minMinutes = breakTile ? 5 : Math.round(perEntry * (dogClass.entries || 0) * 10) / 10;
        const previous = tileIndex > 0 ? judgeSlot.tiles[tileIndex - 1] : undefined;
        const slackBeforeMinutes = previous && isGapBreak(previous.dogClass) ? (previous.dogClass.duration || 0) : 0;
        note(tileStart);
        note(end);
        events.push({
          id: `evt:${classId}`,
          resourceId: classId,
          title: barTitle,
          start: tileStart.toISOString(),
          end: end.toISOString(),
          editable: true,
          durationEditable: breakTile,
          resourceEditable: true,
          startEditable: true,
          overlap: true,
          classNames: [breakTile ? "jsb-break" : "jsb-class", children.length ? "jsb-grouped" : ""].filter(Boolean),
          extendedProps: {
            kind: breakTile ? "break" : "class",
            showId: tile.showId,
            ring: tile.ring,
            judgeIndex: tile.judgeIndex,
            classIndex: tile.classIndex,
            tooltip: classTooltip(event, show, dogClass, startLabel, children),
            minMinutes,
            minutes: tile.minutes,
            startMs: tileStart.getTime(),
            slackBeforeMinutes,
            judgeKey: judgeSlot.key,
          },
        });
      });
    });
  });
}
